// The sponsor: BailaBento (DESIGN §10).
//
// THIS IS THE ONLY PLACE TO EDIT THE SPONSOR'S WORDS AND LINKS. Every sentence the interface says about BailaBento, the name, the
// address and the way a link is tagged are here; the interface (sponsor-ui.tsx, home.tsx, sheets.tsx, stage.tsx, share.ts) only
// places them. index.html and the manifest are static files that cannot import this one, so a unit test (sponsor.test.ts) checks
// that the page description, the Open Graph and Twitter descriptions and the manifest's description end with `SHARED_TAG`.
//
// No cookies, no third-party scripts, no analytics: a link is an ordinary link, and the only trace of salsacount in it is the
// `utm_` tags below, which BailaBento reads on its own site. Nothing about the user or their music is in a link.

/** The name, in this capitalisation, everywhere. */
export const NAME = 'BailaBento';

/** The sponsor's landing page, live since 2026-10-04 (DESIGN §10). A link is this address and the three `utm_` tags. */
export const BASE_URL = 'https://bailabento.com/login';

/** Where a link is: `utm_content`. `tell_an_organiser` is the link inside the share message (`utm_medium=referral`); the rest are in the app (`utm_medium=app`). */
export type Placement = 'home_card' | 'home_title' | 'menu' | 'song_end' | 'about' | 'tell_an_organiser';

/** The tagged address for a placement. The same `utm_source` for all; `utm_medium` is `referral` for the share message and `app` for the rest. */
export function sponsorLink(placement: Placement): string {
  const medium = placement === 'tell_an_organiser' ? 'referral' : 'app';
  return `${BASE_URL}?utm_source=salsacount&utm_medium=${medium}&utm_content=${placement}`;
}

/** The accessible name of a link that opens in a new tab: what the sighted reader sees, then where it goes. */
export function linkName(visible: string): string {
  return `${visible}, opens in a new tab`;
}

// ── the words ──

/** Home, under the title, beside the tagline: a link. */
export const HOME_BYLINE = 'by BailaBento';

/** Home sponsor card. */
export const CARD = {
  label: 'Free, thanks to',
  name: NAME,
  line: 'Competition and congress management a small dance school can afford: registration, schedules, judging, results.',
  see: 'Running an event? See BailaBento',
  send: 'Know an organiser? Send this',
} as const;

/** The Send this action: the confirmation when the message was copied (no share sheet here), and the words if even copying failed. */
export const SEND_COPIED = 'Message copied. Paste it to whoever runs your events.';
export const SEND_COPY_FAILED = 'Copy this message:';

/** Menu sheet, last line: a link. */
export const MENU_LINK = 'Free, thanks to BailaBento';

/** End of a song, in the stage's status line; the name in the middle is the link. */
export const SONG_END = { before: "That's the song. salsacount is free, thanks to ", link: NAME, after: '.' } as const;
export const SONG_END_TEXT = `${SONG_END.before}${SONG_END.link}${SONG_END.after}`;

/** About sheet: three plain sentences (why it is free; what BailaBento does; who it is priced for), then the link. */
export const ABOUT = {
  sentences: [
    'salsacount is free because BailaBento makes it.',
    'BailaBento is management software for dance competitions and congresses: registration, schedules, judging and results.',
    'It is priced for small schools and small events.',
  ],
  link: 'Visit BailaBento',
} as const;

/** The share message (to an organiser), which the link follows. */
export const SHARE_MESSAGE = 'If you run dance events: BailaBento handles registration, schedules, judging and results, at a price a small school can afford.';
export const SHARE_TITLE = NAME;

/** Everything salsacount shares ends with this: its own share message, the page and card descriptions, the manifest's description. */
export const SHARED_TAG = 'Free, by BailaBento.';
